import { gql } from '@apollo/client';

export const DELIVERABLE_METADATA_FRAGMENT = gql`
  fragment DeliverableMetadata on Deliverable {
    id
    creditFacilityId
    creditFacilityName
    creditFacilitySlug
    currentSubmission {
      ... on DeliverableFileSubmission {
        fileName
        created_at
      }
    }
    deliverableType
    description
    dueDate
    expectedValueConfig {
      ... on DeliverableExpectedValueConfigNoSubmission {
        valueType
      }
      ... on DeliverableExpectedValueConfigNonFile {
        valueType
      }
      ... on DeliverableExpectedValueConfigFile {
        valueType
      }
    }
    name
    section
    status
    updated_at
  }
`;

export const DELIVERABLE_FRAGMENT = gql`
  fragment Deliverable on Deliverable {
    id
    configKey
    creditFacilityId
    creditFacilitySlug
    creditFacilityName
    expectedValueConfig {
      ... on DeliverableExpectedValueConfigNoSubmission {
        valueType
      }
      ... on DeliverableExpectedValueConfigNonFile {
        valueType
      }
      ... on DeliverableExpectedValueConfigFile {
        valueType
        fileTypes
      }
    }
    name
    description
    dueDate
    status
    section
    updated_at
    created_at
    mostRecentVersion {
      isMostRecentDeliverableVersion
      mostRecentDeliverableId
    }
  }
`;

export const DELIVERABLE_FILE_SUBMISSION_FRAGMENT = gql`
  fragment DeliverableFileSubmission on DeliverableFileSubmission {
    id
    deliverableId
    fileName
    value
    valueType
    created_at
  }
`;

export const DELIVERABLE_VALUE_SUBMISSION_FRAGMENT = gql`
  fragment DeliverableValueSubmission on DeliverableValueSubmission {
    id
    deliverableId
    value
    valueType
    created_at
  }
`;

export const DELIVERABLE_DEPENDENCY_FRAGMENT = gql`
  fragment DeliverableDependency on DeliverableDependency {
    deliverable {
      id
      name
      dueDate
      status
      currentSubmission {
        ... on DeliverableFileSubmission {
          ...DeliverableFileSubmission
        }
        ... on DeliverableValueSubmission {
          ...DeliverableValueSubmission
        }
      }
      updated_at
    }
  }
`;

export const DELIVERABLE_RESPONSIBLE_PARTY_FRAGMENT = gql`
  fragment DeliverableResponsibleParty on DeliverableResponsibleParty {
    responsibleParty {
      id
      name
    }
  }
`;

export const GET_DELIVERABLES_FRAGMENT = gql`
  fragment GetDeliverablesDeliverableData on Deliverable {
    ...DeliverableMetadata
    isDeliverableResponsibleParty
    isDeliverableDownloadPermissionedParty
    isDeliverableViewPermissionedParty
    # @jendevelops TODO: add responsible parties (need to see how we can permission who sees what owners)
    responsibleParties {
      responsibleParty {
        id
        name
      }
    }
    # TODO chris, we will eventually need a better way of generating dependencies rather than field resolvers per deliverable which will become expensive over time
    dependencies {
      ...DeliverableDependency
    }
  }
`;

export const GET_DELIVERABLES = gql`
  ${GET_DELIVERABLES_FRAGMENT}
  query GetDeliverables(
    $responsiblePartyId: UUID!
    $organizationId: UUID
    $creditFacilityIds: [UUID!]
    $limit: Int
    $statuses: [DeliverableProgressStatus!]
    $offset: Int
    $startDate: JSDate
    $endDate: JSDate
  ) {
    deliverables(
      responsiblePartyId: $responsiblePartyId
      organizationId: $organizationId
      creditFacilityIds: $creditFacilityIds
      limit: $limit
      statuses: $statuses
      offset: $offset
      startDate: $startDate
      endDate: $endDate
    ) {
      nodes {
        ...GetDeliverablesDeliverableData
      }
      offsetInfo {
        hasNextPage
        hasPreviousPage
        offset
      }
      totalCount
    }
  }
`;

export const GET_DELIVERABLE = gql`
  ${DELIVERABLE_FRAGMENT}
  ${DELIVERABLE_DEPENDENCY_FRAGMENT}
  ${DELIVERABLE_FILE_SUBMISSION_FRAGMENT}
  ${DELIVERABLE_VALUE_SUBMISSION_FRAGMENT}
  # ${DELIVERABLE_RESPONSIBLE_PARTY_FRAGMENT}
  query GetDeliverable($deliverableId: UUID!) {
    deliverable(deliverableId: $deliverableId) {
      ...Deliverable
      creditAgreementDocumentId

      # Field Resolvers
      creditFacilityName
      isDeliverableResponsibleParty
      isDeliverableDownloadPermissionedParty
      isDeliverableViewPermissionedParty
        # @jendevelops TODO: add responsible parties (need to see how we can permission who sees what owners)
      responsibleParties {
        responsibleParty {
          id
          name
        }
      }
      dependencies {
        ...DeliverableDependency
      }
      submissions {
        ... on DeliverableFileSubmission {
          ...DeliverableFileSubmission
        }
        ... on DeliverableValueSubmission {
          ...DeliverableValueSubmission
        }
      }
      currentSubmission {
        ... on DeliverableFileSubmission {
          ...DeliverableFileSubmission
        }
        ... on DeliverableValueSubmission {
          ...DeliverableValueSubmission
        }
      }
      events {
        id
        actorName
        targetName
        domainKey
        actionKey
        created_at
        payload {
          ... on InsertedDeliverablePayload {
            deliverable {
              name
              creditFacilityName
            }
          }

          ... on UpdatedDeliverableStatusPayload {
            previousStatus
            deliverable {
              status
              creditFacilityName
            }
          }

          ... on SubmittedDeliverableSubmissionPayload {
            deliverable {
              creditFacilityName
            }
          }
        }
      }
    }
  }
`;

export const SUBMIT_DELIVERABLE_SUBMISSION = gql`
  mutation SubmitDeliverableSubmission(
    $id: UUID!
    $deliverableId: UUID!
    $value: String!
    $valueType: DeliverableValueType!
  ) {
    submitDeliverableSubmission(
      id: $id
      deliverableId: $deliverableId
      value: $value
      valueType: $valueType
    ) {
      ... on DeliverableFileSubmission {
        id
        value
        valueType
        deliverable {
          # need ID and status for cacheing keyArg
          id
          name
          status
          dueDate
        }
      }
      ... on DeliverableValueSubmission {
        id
        value
        valueType
        deliverable {
          # need ID and status for cacheing keyArg
          id
          name
          status
          dueDate
        }
      }
    }
  }
`;

export const UPDATE_DELIVERABLE_STATUS = gql`
  mutation UpdateDeliverableStatus(
    $deliverableId: UUID!
    $status: DeliverableProgressStatus!
  ) {
    updateDeliverableStatus(deliverableId: $deliverableId, status: $status) {
      id
      status
      name
    }
  }
`;

export const GET_DELIVERABLE_SUBMISSION_DOWNLOAD_URL = gql`
  query GetDeliverableSubmissionDownloadUrl(
    $deliverableSubmissionId: UUID!
    $deliverableId: UUID!
  ) {
    deliverable(deliverableId: $deliverableId) {
      id
      status
      submissions(deliverableSubmissionId: $deliverableSubmissionId) {
        ... on DeliverableFileSubmission {
          submissionDownloadUrl
        }
      }
    }
  }
`;

export const GET_CURRENT_DELIVERABLE_SUBMISSION_DOWNLOAD_URL = gql`
  query GetCurrentDeliverableSubmissionDownloadUrl($deliverableId: UUID!) {
    deliverable(deliverableId: $deliverableId) {
      id
      status
      currentSubmission {
        ... on DeliverableFileSubmission {
          submissionDownloadUrl
        }
      }
    }
  }
`;
